import {
    initializeFaro,
    getWebInstrumentations
} from '@grafana/faro-react'
import { getFaroIgnoreUrls } from 'services/getFaroIgnoreUrls'
import { buildTracingInstrumentation } from 'services/faroTracingSetup'
import {
    pushFaroDeviceContextEvent,
    syncFaroClassroomContext,
    syncFaroClassroomContextFromStorage
} from 'services/faroUserContext'

// Reads classroomId and userId from the autologin URL hash if present.
// Called synchronously after initializeFaro so TTFB/FCP events carry classId.
// Returns true if context was extracted from the URL, false otherwise.
const extractClassroomContextFromUrl = (): boolean => {
    const hash = window.location.hash
    const match = hash.match(/^#\/autologin\/([^/]+)\/([^/]+)/)
    if (!match) return false
    const classId = decodeURIComponent(match[1])
    const userId = decodeURIComponent(match[2])
    if (classId && userId) {
        syncFaroClassroomContext({ userId, classId })
        return true
    }
    return false
}

// Fallback for page refresh: sessionStorage already has userId but BookingReducer
// won't fire set-classroom again, so we sync user context into Faro from storage.
// Only used when the page is not an autologin URL (e.g. direct refresh on /diagnostics).
const syncFaroUserContext = async (maxWaitTime = 20000, interval = 1000): Promise<void> => {
    return new Promise(resolve => {
        const startTime = Date.now()

        const checkStorage = (): void => {
            if (syncFaroClassroomContextFromStorage()) {
                resolve()
            } else if (Date.now() - startTime < maxWaitTime) {
                setTimeout(checkStorage, interval)
            } else {
                resolve()
            }
        }

        checkStorage()
    })
}

const initFaroConfig = async (): Promise<void> => {
    if (!process.env.REACT_APP_FARO_URL) {
        console.warn('REACT_APP_FARO_URL is not set — Faro disabled')
        return
    }

    try {
        initializeFaro({
            url: process.env.REACT_APP_FARO_URL,
            ignoreUrls: getFaroIgnoreUrls(),
            app: {
                name: process.env.REACT_APP_FARO_PROJECT ?? 'unknown-app',
                version: '1.0.0',
                environment: process.env.REACT_APP_ENV ?? 'unknown'
            },
            sessionTracking: {
                enabled: true,
                persistent: true
            },
            instrumentations: [
                ...getWebInstrumentations(),
                buildTracingInstrumentation()
            ]
        })

        pushFaroDeviceContextEvent()

        const contextFromUrl = extractClassroomContextFromUrl()

        if (!contextFromUrl) {
            void syncFaroUserContext()
        }
    } catch (error) {
        console.error('Error initializing Faro:', error)
    }
}

void initFaroConfig()
